/**
 * Payment terms, chosen per invoice at send time.
 *
 * The office picks one on the send dialog; the choice is persisted on the
 * invoice and printed on the PDF as a `Terms` meta row plus a footer sentence.
 * There is deliberately no client-level default — the client asked to set terms
 * per customer *when sending*, and a stored default would silently apply itself
 * to invoices nobody re-read.
 *
 * The wording below is the CLIENT'S, verbatim, down to the surrounding
 * parentheses. Do not paraphrase it: it is the sentence they want their
 * customers to read, and the late-fee clause is the part that does work.
 */
export const PAYMENT_TERMS = ['due_on_receipt', 'due_15_days', 'due_30_days'] as const;
export type PaymentTerms = (typeof PAYMENT_TERMS)[number];

/**
 * Days from the INVOICE DATE to the due date.
 *
 * Counted from the invoice date and not from the send date, because that is
 * what the printed sentence promises the customer — "within 30 days of the
 * invoice date". A due date measured from anything else would contradict the
 * document it appears on.
 *
 * The single source for both sides: `dueDateFor()` below reads it, and
 * `buildDueDateCase()` in backend/src/services/invoice-ledger.ts emits its SQL
 * CASE from it, the same way the lifecycle and aging rules are emitted.
 */
export const PAYMENT_TERMS_DAYS: Record<PaymentTerms, number> = {
  due_on_receipt: 0,
  due_15_days: 15,
  due_30_days: 30,
};

export const PAYMENT_TERMS_LABELS: Record<PaymentTerms, string> = {
  due_on_receipt: 'Due Upon Receipt',
  due_15_days: 'Due in 15 Days',
  due_30_days: 'Due in 30 Days',
};

/** The fine print printed beneath the invoice body for each choice. */
export const PAYMENT_TERMS_NOTES: Record<PaymentTerms, string> = {
  due_on_receipt: '(Payment is due upon receipt of this invoice.)',
  due_15_days:
    '(Please remit payment within 15 days of the invoice date to avoid any applicable late fees.)',
  due_30_days:
    '(Please remit payment within 30 days of the invoice date to avoid any applicable late fees.)',
};

/** What a cash invoice prints instead of bank/remit instructions. */
export const CASH_PAYEE_NAME = 'Clifford Lo';
export const CASH_INVOICE_NOTE = `Please make check payable to ${CASH_PAYEE_NAME}`;

export function isPaymentTerms(value: unknown): value is PaymentTerms {
  return typeof value === 'string' && (PAYMENT_TERMS as readonly string[]).includes(value);
}

/**
 * The due date of an invoice, as 'YYYY-MM-DD', or null when it has no terms.
 *
 * NULL IS A REAL ANSWER, not a gap to paper over. An invoice nobody has set
 * terms on has no date it is due by, and the overdue rule falls back to
 * `overdue_after_days` for exactly those rows — inventing a date here would
 * silently make every legacy invoice overdue on a day no one chose.
 *
 * Date-only arithmetic, done in UTC on purpose: `invoiceDate` is a calendar
 * date with no time and no zone, and constructing it as local midnight then
 * adding days would shift the result across a DST boundary.
 */
export function dueDateFor(
  invoiceDate: string,
  terms: PaymentTerms | null | undefined,
): string | null {
  if (!isPaymentTerms(terms)) return null;
  const due = new Date(`${invoiceDate}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return null;
  due.setUTCDate(due.getUTCDate() + PAYMENT_TERMS_DAYS[terms]);
  return due.toISOString().slice(0, 10);
}
